/**
 * LlmBudget · iter v29 r004
 *
 * Pannello founder-only per monitorare il consumo della Universal Key
 * Emergent in tempo reale. Mobile-first: l'utente vuole controllare il
 * budget dal cellulare.
 *
 * Mostra:
 *  • Spesa di OGGI (USD + EUR)
 *  • Spesa del mese
 *  • Proiezione fine mese (al ritmo attuale)
 *  • Budget impostato e % usata
 *  • Alert (ok / warning / critical) con CTA
 *  • Breakdown per azione
 *  • Top 5 utenti consumatori
 *  • Quick CTA "Vai su Profilo Emergent · Aggiungi budget"
 *
 * Route: /dashboard/admin/budget (founder gating tramite endpoint).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Lightning, Wallet, ChartLineUp, Warning, CheckCircle, ArrowSquareOut,
  ArrowsClockwise, Eye, FloppyDisk,
} from "@phosphor-icons/react";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtEur = (n) => `€${Number(n || 0).toFixed(2)}`;

const ALERT_STYLES = {
  ok:       { bg: "bg-[#F1F4ED]", text: "text-[#3F5841]", border: "border-[#7C8B6E]/30", Icon: CheckCircle, iconColor: "text-[#7C8B6E]" },
  warning:  { bg: "bg-[#FDF4E0]", text: "text-[#7C5E1E]", border: "border-[#D4A04A]/40", Icon: Warning,     iconColor: "text-[#D4A04A]" },
  critical: { bg: "bg-[#FCE9E4]", text: "text-[#7C2418]", border: "border-[#C45A3E]/40", Icon: Warning,     iconColor: "text-[#C45A3E]" },
};

function MetricCard({ label, value, sub, Icon, testid, accent = false }) {
  return (
    <div
      className={`p-4 sm:p-5 rounded-xl border ${accent ? "bg-[#1C1C1A] text-[#FAF8F4] border-[#1C1C1A]" : "bg-white border-[#E5E7E0]"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className={`text-[10.5px] uppercase tracking-[0.2em] font-semibold ${accent ? "text-[#FAF8F4]/70" : "text-[#6B6A65]"}`}>
          {label}
        </div>
        {Icon && (
          <Icon
            size={16}
            weight="duotone"
            className={accent ? "text-[#A8B89A]" : "text-[#9A968B]"}
          />
        )}
      </div>
      <div className={`mt-2 font-editorial tracking-tight leading-none`} style={{ fontSize: 30 }}>
        {value}
      </div>
      {sub && <div className={`text-[11px] mt-1.5 ${accent ? "text-[#FAF8F4]/70" : "text-[#9A968B]"}`}>{sub}</div>}
    </div>
  );
}

export default function LlmBudget() {
  const [data, setData] = useState(null);
  const [heavyUsers, setHeavyUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/llm-budget");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // Heavy users 24h — chiamata separata per non bloccare il pannello budget
    try {
      const { data: hu } = await api.get("/admin/heavy-users?hours=24");
      setHeavyUsers(hu);
    } catch { /* silenzioso */ }
  };

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    load();
    // Refresh ogni 60s mentre stai sulla pagina
    const tid = setInterval(load, 60000);
    return () => clearInterval(tid);
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center text-[#6B6A65]">
          Caricamento budget…
        </div>
      </AppShell>
    );
  }
  if (forbidden) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center text-[#9A968B] text-sm">
          Accesso riservato.
        </div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center text-[#9A968B]">Errore caricamento dati.</div>
      </AppShell>
    );
  }

  const alert = ALERT_STYLES[data.alert.level] || ALERT_STYLES.ok;
  const AlertIcon = alert.Icon;
  const pct = Math.min(100, data.budget.pct_used);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto pb-12 px-2 sm:px-0">
        {/* ── Heading ── */}
        <div className="pt-6 sm:pt-8 mb-6 sm:mb-8 flex items-end justify-between gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#9A968B] font-semibold">Pannello founder</div>
            <h1 className="font-editorial text-[28px] sm:text-[40px] tracking-tight text-[#1C1C1A] leading-tight mt-1">
              Consumo <span className="italic text-[#7C8B6E]">Universal Key</span>
            </h1>
            <p className="text-[13px] text-[#6B6A65] mt-2 max-w-xl">
              Quanto stiamo bruciando in chiamate ai modelli (Claude · Gemini Nano Banana · Whisper) sulla
              Universal Key Emergent. Le stime sono conservative.
            </p>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E5E7E0] text-[12px] font-semibold text-[#1C1C1A] hover:bg-[#FAFAF7] disabled:opacity-60"
            data-testid="budget-refresh-btn"
          >
            <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
            Aggiorna
          </button>
        </div>

        {/* ── Alert ── */}
        <div
          className={`mb-6 p-4 sm:p-5 rounded-xl border ${alert.bg} ${alert.border} flex items-start gap-3`}
          data-testid="budget-alert"
        >
          <AlertIcon size={22} weight="fill" className={`shrink-0 mt-0.5 ${alert.iconColor}`} />
          <div className="flex-1">
            <div className={`text-[13.5px] font-semibold leading-snug ${alert.text}`}>{data.alert.message}</div>
            {data.alert.level !== "ok" && (
              <a
                href="https://app.emergent.sh/profile"
                target="_blank"
                rel="noreferrer"
                className={`mt-2 inline-flex items-center gap-1 text-[12px] font-semibold underline ${alert.text}`}
                data-testid="budget-topup-link"
              >
                Vai al profilo Emergent · aggiungi budget <ArrowSquareOut size={11} weight="bold" />
              </a>
            )}
          </div>
        </div>

        {/* ── Saldo reale (sync manuale) ── */}
        <RealBalanceBox real={data.real_balance} onSynced={load} />

        {/* ── Errori recenti (key esaurita) ── */}
        {data.recent_failures && data.recent_failures.length > 0 && (
          <RecentFailuresBox failures={data.recent_failures} />
        )}

        {/* ── 4 metric cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <MetricCard
            label="Oggi"
            value={fmtUsd(data.today.spend_usd)}
            sub={`${fmtEur(data.today.spend_eur)} · ${data.today.calls} call`}
            Icon={Lightning}
            testid="budget-today"
          />
          <MetricCard
            label="Questo mese"
            value={fmtUsd(data.month.spend_usd)}
            sub={`${fmtEur(data.month.spend_eur)} · ${data.month.calls} call · giorno ${data.month.days_elapsed}/${data.month.days_in_month}`}
            Icon={Wallet}
            testid="budget-month"
          />
          <MetricCard
            label="A fine mese (proiezione)"
            value={fmtUsd(data.projection.month_end_usd)}
            sub={`${fmtEur(data.projection.month_end_eur)} · media ${fmtUsd(data.projection.avg_daily_usd)}/giorno`}
            Icon={ChartLineUp}
            testid="budget-projection"
            accent
          />
          <MetricCard
            label="Budget impostato"
            value={fmtUsd(data.budget.threshold_usd)}
            sub={`${fmtEur(data.budget.threshold_eur)} · resta ${fmtUsd(data.budget.remaining_usd)}`}
            testid="budget-threshold"
          />
        </div>

        {/* ── Progress bar utilizzo ── */}
        <div className="mb-8 p-5 rounded-xl bg-white border border-[#E5E7E0]" data-testid="budget-progress">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">Utilizzo del budget mensile</div>
            <div className={`text-[14px] font-semibold ${pct >= 90 ? "text-[#C45A3E]" : pct >= 60 ? "text-[#D4A04A]" : "text-[#7C8B6E]"}`}>
              {pct.toFixed(1)} %
            </div>
          </div>
          <div className="h-3 rounded-full bg-[#F1F4ED] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${pct >= 90 ? "bg-[#C45A3E]" : pct >= 60 ? "bg-[#D4A04A]" : "bg-[#7C8B6E]"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-3 text-[11.5px] text-[#6B6A65] leading-snug">
            La barra mostra la proiezione di spesa a fine mese (al ritmo attuale) rispetto al budget impostato.
            Default <strong>$220/mese ≈ €200</strong>, allineato col cap storico della piattaforma.
            Per cambiarlo, modifica <code className="px-1 py-0.5 rounded bg-[#F1F4ED] font-mono text-[10.5px]">LLM_BUDGET_USD_MONTH</code> in backend/.env.
            <br/>
            Riceverai un&apos;<strong>email automatica</strong> ai founder quando arrivi al 60% (warning) o al 90% (critico). Max 1 email/livello al giorno.
          </div>
        </div>

        {/* ── Breakdown per azione ── */}
        <div className="mb-8">
          <h2 className="text-[15px] font-semibold text-[#1C1C1A] mb-3">Consumo per servizio</h2>
          {data.by_action.length === 0 ? (
            <div className="p-5 rounded-xl bg-white border border-[#E5E7E0] text-[#9A968B] text-[13px]">
              Nessuna chiamata registrata in questo mese.
            </div>
          ) : (
            <div className="rounded-xl bg-white border border-[#E5E7E0] divide-y divide-[#F0EDE5] overflow-hidden">
              {data.by_action.map((row) => {
                const monthMax = data.month.spend_usd || 1;
                const w = Math.max(2, (row.spend_usd / monthMax) * 100);
                return (
                  <div key={row.action} className="p-3.5 sm:p-4 flex items-center gap-3" data-testid={`budget-action-${row.action}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3 mb-1.5">
                        <div className="text-[13.5px] font-semibold text-[#1C1C1A] truncate">{row.label}</div>
                        <div className="text-[12px] text-[#1C1C1A] font-semibold shrink-0 tabular-nums">{fmtUsd(row.spend_usd)}</div>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#F1F4ED] overflow-hidden">
                        <div className="h-full rounded-full bg-[#7C8B6E]" style={{ width: `${w}%` }} />
                      </div>
                      <div className="mt-1.5 text-[10.5px] text-[#9A968B] flex gap-3">
                        <span>{row.calls} call</span>
                        <span>~{fmtUsd(row.avg_per_call_usd)}/call</span>
                        <span>{fmtEur(row.spend_eur)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Top 5 utenti consumatori ── */}
        {data.top_users.length > 0 && (
          <div className="mb-8">
            <h2 className="text-[15px] font-semibold text-[#1C1C1A] mb-3">Top 5 utenti questo mese</h2>
            <div className="rounded-xl bg-white border border-[#E5E7E0] divide-y divide-[#F0EDE5] overflow-hidden">
              {data.top_users.map((u, i) => (
                <div key={u.user_id} className="p-3.5 sm:p-4 flex items-center gap-3" data-testid={`budget-user-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-[#F1F4ED] flex items-center justify-center text-[12px] font-semibold text-[#5A6A4D] shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-[#1C1C1A] truncate">{u.email}</div>
                    <div className="text-[11px] text-[#9A968B] truncate">{u.agent_name || "—"} · piano {u.plan}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-semibold text-[#1C1C1A] tabular-nums">{fmtUsd(u.spend_usd)}</div>
                    <div className="text-[10.5px] text-[#9A968B]">{u.calls} call</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Heavy users 24h (anti-abuso) ── iter v29 r273 */}
        {heavyUsers && heavyUsers.users?.length > 0 && (
          <div className="mb-8" data-testid="heavy-users-section">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-[15px] font-semibold text-[#1C1C1A]">Utenti pesanti · ultime 24h</h2>
              <div className="text-[11px] text-[#9A968B]">
                {heavyUsers.summary.red_flagged > 0 && (
                  <span className="text-[#C45A3E] font-semibold mr-2">{heavyUsers.summary.red_flagged} rossi</span>
                )}
                {heavyUsers.summary.yellow_flagged > 0 && (
                  <span className="text-[#D4A04A] font-semibold mr-2">{heavyUsers.summary.yellow_flagged} gialli</span>
                )}
                <span>tot {heavyUsers.summary.total_users_active}</span>
              </div>
            </div>
            <div className="rounded-xl bg-white border border-[#E5E7E0] divide-y divide-[#F0EDE5] overflow-hidden">
              {heavyUsers.users.slice(0, 15).map((u, i) => {
                const dotColor = u.level === "red" ? "bg-[#C45A3E]" : u.level === "yellow" ? "bg-[#D4A04A]" : "bg-[#7C8B6E]";
                return (
                  <div key={u.user_id} className="p-3.5 sm:p-4 flex items-center gap-3" data-testid={`heavy-user-${i}`}>
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotColor}`} aria-label={`flag ${u.level}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-semibold text-[#1C1C1A] truncate">{u.email}</div>
                      <div className="text-[11px] text-[#9A968B] truncate">
                        piano <strong>{u.plan}</strong> · iscritto {u.joined || "?"}
                      </div>
                    </div>
                    <div className="hidden sm:flex gap-3 text-[11px] text-[#6B6A65] tabular-nums">
                      <span title="sopralluoghi finalizzati 24h"><strong>{u.sopralluoghi_24h}</strong> sopr.</span>
                      <span title="foto caricate 24h"><strong>{u.foto_24h}</strong> foto</span>
                      <span title="chiamate LLM totali 24h"><strong>{u.calls_24h}</strong> LLM</span>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] font-semibold text-[#1C1C1A] tabular-nums">{fmtUsd(u.spend_24h_usd)}</div>
                      <div className="text-[10.5px] text-[#9A968B]">in 24h</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[10.5px] text-[#9A968B] leading-snug">
              🔴 Rosso = abuso confermato (≥30 sopr. · ≥100 foto · ≥200 chiamate LLM · ≥$5 di costo).
              🟡 Giallo = da monitorare (≥15 sopr. · ≥50 foto · ≥100 chiamate · ≥$2.5).
              Se vedi rossi ricorrenti: contatta l&apos;utente o forza il piano superiore.
            </div>
          </div>
        )}

        {/* ── Note finali ── */}
        <div className="text-[11px] text-[#9A968B] leading-relaxed border-t border-[#E5E7E0] pt-5 mt-8">
          Stima conservativa. I costi reali dipendono dal listino Emergent (Claude Sonnet $3/$15 per 1M token,
          Gemini Nano Banana $0.039/immagine, Whisper $0.006/min). Aggiornato live ogni 60 secondi.
        </div>
      </div>
    </AppShell>
  );
}

/* ─────────────────────────────────────────────────────────────
 * RealBalanceBox · sync manuale del saldo Emergent reale
 *
 * Il founder apre app.emergent.sh dal cellulare, legge i due numeri
 * ("Used: $X / Budget: $Y") e li incolla qui. Il sistema usa quelli
 * come baseline + traccia il delta locale dopo il sync.
 * ───────────────────────────────────────────────────────────── */
function RealBalanceBox({ real, onSynced }) {
  const [editing, setEditing] = useState(false);
  const [used, setUsed] = useState("");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (real && !real.pending) {
      setUsed(String(real.used_at_sync_usd ?? ""));
      setBudget(String(real.budget_at_sync_usd ?? ""));
    }
  }, [real]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSave = async () => {
    setError("");
    const u = parseFloat(String(used).replace(",", "."));
    const b = parseFloat(String(budget).replace(",", "."));
    if (!Number.isFinite(u) || u < 0) { setError("Used $ non valido"); return; }
    if (!Number.isFinite(b) || b <= 0) { setError("Budget $ non valido"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/llm-budget/sync", { used_usd: u, budget_usd: b });
      if (r?.data?.ok) {
        setEditing(false);
        onSynced && onSynced();
      } else {
        setError(r?.data?.error || "Errore salvataggio");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const formatSyncDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMin = Math.round((now - d) / 60000);
      if (diffMin < 1) return "adesso";
      if (diffMin < 60) return `${diffMin} min fa`;
      const diffH = Math.round(diffMin / 60);
      if (diffH < 24) return `${diffH} h fa`;
      return d.toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const showEmpty = real?.pending || editing;

  return (
    <div className="mb-6 p-4 sm:p-5 rounded-xl bg-white border-2 border-[#1C1C1A]" data-testid="real-balance-box">
      <div className="flex items-start gap-3 mb-3">
        <Eye size={20} weight="duotone" className="text-[#1C1C1A] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#1C1C1A]">
            Saldo reale Emergent
          </div>
          <div className="text-[11.5px] text-[#6B6A65] mt-0.5 leading-snug">
            Apri <a href="https://app.emergent.sh" target="_blank" rel="noreferrer" className="underline text-[#7C8B6E] font-semibold">app.emergent.sh</a> dal cellulare,
            tocca <strong>icona profilo → Universal Key</strong>, copia &ldquo;Used&rdquo; e &ldquo;Budget&rdquo; qui sotto.
            Se non vedi il profilo, attiva &ldquo;Richiedi sito desktop&rdquo; da Safari/Chrome.
          </div>
        </div>
      </div>

      {showEmpty ? (
        <div className="space-y-3" data-testid="real-balance-edit">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1">
                Used (USD)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={used}
                onChange={(e) => setUsed(e.target.value)}
                placeholder="es. 23.45"
                className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7E0] text-[16px] font-semibold tabular-nums bg-white focus:outline-none focus:border-[#7C8B6E]"
                data-testid="real-balance-used-input"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1">
                Budget (USD)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="es. 220"
                className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7E0] text-[16px] font-semibold tabular-nums bg-white focus:outline-none focus:border-[#7C8B6E]"
                data-testid="real-balance-budget-input"
              />
            </div>
          </div>
          {error && <div className="text-[12px] text-[#C45A3E] font-semibold">{error}</div>}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#1C1C1A] text-[#FAF8F4] text-[13px] font-semibold disabled:opacity-60"
              data-testid="real-balance-save-btn"
            >
              <FloppyDisk size={14} weight="fill" /> {saving ? "Salvo..." : "Salva sync"}
            </button>
            {!real?.pending && (
              <button
                onClick={() => { setEditing(false); setError(""); }}
                className="px-4 py-2.5 rounded-lg border border-[#E5E7E0] text-[13px] font-semibold text-[#6B6A65]"
              >
                Annulla
              </button>
            )}
          </div>
        </div>
      ) : (
        <div data-testid="real-balance-view">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1">Speso (vero+delta)</div>
              <div className="text-[20px] sm:text-[24px] font-semibold tabular-nums text-[#1C1C1A] leading-none">
                ${real.estimated_now_usd}
              </div>
              <div className="text-[10.5px] text-[#9A968B] mt-1">€{real.estimated_now_eur}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1">Rimanente</div>
              <div className={`text-[20px] sm:text-[24px] font-semibold tabular-nums leading-none ${
                real.pct_used >= 90 ? "text-[#C45A3E]" : real.pct_used >= 60 ? "text-[#D4A04A]" : "text-[#7C8B6E]"
              }`}>
                ${real.remaining_usd}
              </div>
              <div className="text-[10.5px] text-[#9A968B] mt-1">€{real.remaining_eur}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B] mb-1">% usato</div>
              <div className={`text-[20px] sm:text-[24px] font-semibold tabular-nums leading-none ${
                real.pct_used >= 90 ? "text-[#C45A3E]" : real.pct_used >= 60 ? "text-[#D4A04A]" : "text-[#7C8B6E]"
              }`}>
                {real.pct_used}%
              </div>
              <div className="text-[10.5px] text-[#9A968B] mt-1">su ${real.budget_at_sync_usd}</div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-[#F1F4ED] overflow-hidden mb-3">
            <div
              className={`h-full rounded-full ${real.pct_used >= 90 ? "bg-[#C45A3E]" : real.pct_used >= 60 ? "bg-[#D4A04A]" : "bg-[#7C8B6E]"}`}
              style={{ width: `${Math.min(100, real.pct_used)}%` }}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] text-[#9A968B] leading-snug">
              Ultimo sync <strong>{formatSyncDate(real.synced_at)}</strong> · da allora +${real.delta_since_sync_usd?.toFixed(2)} stimati
            </div>
            <button
              onClick={() => setEditing(true)}
              className="shrink-0 px-3 py-1.5 rounded-full border border-[#1C1C1A] text-[11.5px] font-semibold text-[#1C1C1A] hover:bg-[#1C1C1A] hover:text-[#FAF8F4] transition-colors"
              data-testid="real-balance-edit-btn"
            >
              Aggiorna sync
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * RecentFailuresBox · ultimi errori "chiave esaurita" (ultimi 7 giorni)
 * ───────────────────────────────────────────────────────────── */
function RecentFailuresBox({ failures }) {
  return (
    <div className="mb-6 p-4 sm:p-5 rounded-xl bg-[#FCE9E4] border border-[#C45A3E]/40" data-testid="recent-failures-box">
      <div className="flex items-start gap-2 mb-2">
        <Warning size={20} weight="fill" className="text-[#C45A3E] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[13px] font-bold text-[#7C2418]">
            {failures.length} {failures.length === 1 ? "errore" : "errori"} &ldquo;Universal Key esaurita&rdquo; negli ultimi 7 giorni
          </div>
          <div className="text-[11.5px] text-[#7C2418]/80 mt-0.5 leading-snug">
            Quando questi appaiono, gli utenti vedono &ldquo;Funzione in manutenzione&rdquo;. Ricarica subito.
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        {failures.map((f, i) => (
          <div key={i} className="text-[11px] text-[#7C2418] flex items-baseline gap-2 bg-white/60 rounded px-2.5 py-1.5">
            <span className="font-semibold tabular-nums shrink-0">{new Date(f.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="font-medium shrink-0">{f.action}</span>
            <span className="text-[#7C2418]/70 truncate flex-1">{f.error_message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

